import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowLeft, HeartPulse, Pill, Stethoscope, Activity, Plus, Trash2, Calendar, Clock, ChevronRight, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SYMPTOM_OPTIONS = [
  'Huvudvärk', 'Trötthet', 'Illamående', 'Yrsel', 'Magont', 'Ryggont',
  'Sömnproblem', 'Ångest', 'Nedstämdhet', 'Hjärtklappning', 'Andnöd',
  'Feber', 'Hosta', 'Ont i halsen', 'Ledvärk', 'Muskelvärk'
];

const TABS = [
  { id: 'medications', label: 'Mediciner', icon: Pill },
  { id: 'symptoms', label: 'Symtom', icon: Activity },
  { id: 'appointments', label: 'Bokningar', icon: Stethoscope },
];

export default function HealthLogPage() {
  const navigate = useNavigate();
  const { hasAccess, getAuthHeader } = useAuth();
  const [activeTab, setActiveTab] = useState('medications');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Medications
  const [medications, setMedications] = useState([]);
  const [showMedForm, setShowMedForm] = useState(false);
  const [newMed, setNewMed] = useState({ name: '', dosage: '', frequency: '', time: '' });
  
  // Symptoms
  const [symptoms, setSymptoms] = useState([]);
  const [showSymptomForm, setShowSymptomForm] = useState(false);
  const [selectedSymptom, setSelectedSymptom] = useState('');
  const [symptomNote, setSymptomNote] = useState('');
  const [symptomIntensity, setSymptomIntensity] = useState(5);
  
  // Appointments
  const [appointments, setAppointments] = useState([]);
  const [showAppForm, setShowAppForm] = useState(false);
  const [newApp, setNewApp] = useState({ type: '', doctor: '', date: '', time: '', location: '', notes: '' });

  useEffect(() => {
    if (hasAccess) {
      loadHealthLog();
    } else {
      setLoading(false);
    }
  }, [hasAccess]);

  const loadHealthLog = async () => {
    try {
      const response = await axios.get(`${API}/health-log`, {
        headers: getAuthHeader()
      });
      const data = response.data;
      setMedications(data.medications || []);
      setSymptoms(data.symptoms || []);
      setAppointments(data.appointments || []);
    } catch (error) {
      console.error('Failed to load health log:', error);
      // Fallback to localStorage
      const savedMeds = localStorage.getItem('health-medications');
      const savedSymptoms = localStorage.getItem('health-symptoms');
      const savedApps = localStorage.getItem('health-appointments');
      if (savedMeds) setMedications(JSON.parse(savedMeds));
      if (savedSymptoms) setSymptoms(JSON.parse(savedSymptoms));
      if (savedApps) setAppointments(JSON.parse(savedApps));
    } finally {
      setLoading(false);
    }
  };

  const saveHealthLog = async (meds, syms, apps) => {
    setSaving(true);
    try {
      await axios.post(`${API}/health-log`, {
        medications: meds,
        symptoms: syms,
        appointments: apps
      }, {
        headers: getAuthHeader()
      });
      // Also save to localStorage as backup
      localStorage.setItem('health-medications', JSON.stringify(meds));
      localStorage.setItem('health-symptoms', JSON.stringify(syms));
      localStorage.setItem('health-appointments', JSON.stringify(apps));
    } catch (error) {
      console.error('Failed to save health log:', error);
      // Fallback to localStorage
      localStorage.setItem('health-medications', JSON.stringify(meds));
      localStorage.setItem('health-symptoms', JSON.stringify(syms));
      localStorage.setItem('health-appointments', JSON.stringify(apps));
    } finally {
      setSaving(false);
    }
  };

  // Medication functions
  const addMedication = async () => {
    if (!newMed.name.trim()) {
      toast.error('Ange medicinens namn');
      return;
    }
    const med = { id: Date.now(), ...newMed, createdAt: new Date().toISOString() };
    const updated = [...medications, med];
    setMedications(updated);
    await saveHealthLog(updated, symptoms, appointments);
    setNewMed({ name: '', dosage: '', frequency: '', time: '' });
    setShowMedForm(false);
    toast.success('Medicin tillagd!');
  };

  const deleteMedication = async (id) => {
    const updated = medications.filter(m => m.id !== id);
    setMedications(updated);
    await saveHealthLog(updated, symptoms, appointments);
    toast.success('Medicin borttagen');
  };

  // Symptom functions
  const addSymptom = async () => {
    if (!selectedSymptom) {
      toast.error('Välj ett symtom');
      return;
    }
    const symptom = {
      id: Date.now(),
      name: selectedSymptom,
      intensity: symptomIntensity,
      note: symptomNote,
      date: new Date().toISOString()
    };
    const updated = [symptom, ...symptoms];
    setSymptoms(updated);
    await saveHealthLog(medications, updated, appointments);
    setSelectedSymptom('');
    setSymptomNote('');
    setSymptomIntensity(5);
    setShowSymptomForm(false);
    toast.success('Symtom loggat!');
  };

  const deleteSymptom = async (id) => {
    const updated = symptoms.filter(s => s.id !== id);
    setSymptoms(updated);
    await saveHealthLog(medications, updated, appointments);
    toast.success('Symtom borttaget');
  };

  // Appointment functions
  const addAppointment = async () => {
    if (!newApp.type.trim() || !newApp.date) {
      toast.error('Ange typ och datum');
      return;
    }
    const app = { id: Date.now(), ...newApp, createdAt: new Date().toISOString() };
    const updated = [...appointments, app].sort((a, b) => new Date(a.date) - new Date(b.date));
    setAppointments(updated);
    await saveHealthLog(medications, symptoms, updated);
    setNewApp({ type: '', doctor: '', date: '', time: '', location: '', notes: '' });
    setShowAppForm(false);
    toast.success('Bokning tillagd!');
  };

  const deleteAppointment = async (id) => {
    const updated = appointments.filter(a => a.id !== id);
    setAppointments(updated);
    await saveHealthLog(medications, symptoms, updated);
    toast.success('Bokning borttagen');
  };

  const getIntensityColor = (intensity) => {
    if (intensity <= 3) return 'bg-green-100 text-green-700';
    if (intensity <= 6) return 'bg-amber-100 text-amber-700';
    return 'bg-red-100 text-red-700';
  };

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-background" data-testid="health-log-page">
        <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/category/health')} className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-xl font-light">Hälsologg</h1>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-6 py-12 text-center">
          <p className="text-muted-foreground">Uppgradera för att få tillgång till hälsologgen.</p>
          <Button onClick={() => navigate('/subscription')} className="rounded-full mt-4">Uppgradera nu</Button>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="health-log-page">
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/category/health')} className="rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-heading text-xl font-light">Hälsologg</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-6 space-y-6">
        <div className="text-center space-y-2">
          <HeartPulse className="w-12 h-12 mx-auto text-emerald-500" />
          <h2 className="font-heading text-2xl font-semibold">Hälsologg</h2>
          <p className="text-muted-foreground">Håll koll på mediciner, symtom och bokningar</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 bg-gray-100 p-1 rounded-xl">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-lg transition-all ${
                activeTab === tab.id ? 'bg-white shadow-sm' : 'hover:bg-gray-50'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              <span className="text-sm font-medium">{tab.label}</span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-emerald-200 border-t-emerald-500 rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Medications Tab */}
            {activeTab === 'medications' && (
              <div className="space-y-4">
                <Button onClick={() => setShowMedForm(true)} className="w-full rounded-full" variant="outline">
                  <Plus className="w-5 h-5 mr-2" />
                  Lägg till medicin
                </Button>

                {showMedForm && (
                  <div className="bg-white rounded-2xl p-6 border border-border/30 space-y-4">
                    <h3 className="font-semibold">Lägg till medicin</h3>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div>
                        <label className="text-sm text-muted-foreground">Namn *</label>
                        <Input
                          value={newMed.name}
                          onChange={(e) => setNewMed({ ...newMed, name: e.target.value })}
                          placeholder="T.ex. Ipren"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-muted-foreground">Dos</label>
                        <Input
                          value={newMed.dosage}
                          onChange={(e) => setNewMed({ ...newMed, dosage: e.target.value })}
                          placeholder="T.ex. 400mg"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-muted-foreground">Frekvens</label>
                        <Input
                          value={newMed.frequency}
                          onChange={(e) => setNewMed({ ...newMed, frequency: e.target.value })}
                          placeholder="T.ex. 2 gånger/dag"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-muted-foreground">Tid</label>
                        <Input
                          value={newMed.time}
                          onChange={(e) => setNewMed({ ...newMed, time: e.target.value })}
                          placeholder="T.ex. Morgon & kväll"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button variant="outline" onClick={() => setShowMedForm(false)} className="rounded-full">
                        Avbryt
                      </Button>
                      <Button onClick={addMedication} className="rounded-full">
                        Lägg till
                      </Button>
                    </div>
                  </div>
                )}

                {medications.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">Inga mediciner registrerade.</p>
                ) : (
                  <div className="space-y-2">
                    {medications.map(med => (
                      <div key={med.id} className="bg-white p-4 rounded-xl border border-border/30 flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center">
                          <Pill className="w-5 h-5" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium">{med.name}</h4>
                          <p className="text-sm text-muted-foreground">
                            {[med.dosage, med.frequency, med.time].filter(Boolean).join(' • ') || 'Ingen info'}
                          </p>
                        </div>
                        <button onClick={() => deleteMedication(med.id)} className="p-2 text-gray-400 hover:text-red-500">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Symptoms Tab */}
            {activeTab === 'symptoms' && (
              <div className="space-y-4">
                <Button onClick={() => setShowSymptomForm(true)} className="w-full rounded-full" variant="outline">
                  <Plus className="w-5 h-5 mr-2" />
                  Logga symtom
                </Button>

                {showSymptomForm && (
                  <div className="bg-white rounded-2xl p-6 border border-border/30 space-y-4">
                    <h3 className="font-semibold">Logga symtom</h3>
                    <div>
                      <label className="text-sm text-muted-foreground mb-2 block">Välj symtom *</label>
                      <div className="flex flex-wrap gap-2">
                        {SYMPTOM_OPTIONS.map(s => (
                          <button
                            key={s}
                            onClick={() => setSelectedSymptom(s)}
                            className={`px-3 py-1 rounded-full text-sm transition-all ${
                              selectedSymptom === s 
                                ? 'bg-emerald-500 text-white' 
                                : 'bg-gray-100 hover:bg-gray-200'
                            }`}
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground mb-2 block">
                        Intensitet: {symptomIntensity}/10
                      </label>
                      <input
                        type="range"
                        min="1"
                        max="10"
                        value={symptomIntensity}
                        onChange={(e) => setSymptomIntensity(Number(e.target.value))}
                        className="w-full"
                      />
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>Lindrigt</span>
                        <span>Måttligt</span>
                        <span>Svårt</span>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm text-muted-foreground">Anteckning (valfritt)</label>
                      <Input
                        value={symptomNote}
                        onChange={(e) => setSymptomNote(e.target.value)}
                        placeholder="T.ex. Efter lunch, hela dagen..."
                      />
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button variant="outline" onClick={() => setShowSymptomForm(false)} className="rounded-full">
                        Avbryt
                      </Button>
                      <Button onClick={addSymptom} className="rounded-full">
                        Logga
                      </Button>
                    </div>
                  </div>
                )}

                {symptoms.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">Inga symtom loggade.</p>
                ) : (
                  <div className="space-y-2">
                    {symptoms.slice(0, 20).map(symptom => (
                      <div key={symptom.id} className="bg-white p-4 rounded-xl border border-border/30 flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getIntensityColor(symptom.intensity)}`}>
                          <span className="font-bold text-sm">{symptom.intensity}</span>
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium">{symptom.name}</h4>
                          <p className="text-sm text-muted-foreground">
                            {new Date(symptom.date).toLocaleDateString('sv-SE', { 
                              weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' 
                            })}
                            {symptom.note && ` • ${symptom.note}`}
                          </p>
                        </div>
                        <button onClick={() => deleteSymptom(symptom.id)} className="p-2 text-gray-400 hover:text-red-500">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Appointments Tab */}
            {activeTab === 'appointments' && (
              <div className="space-y-4">
                <Button onClick={() => setShowAppForm(true)} className="w-full rounded-full" variant="outline">
                  <Plus className="w-5 h-5 mr-2" />
                  Lägg till bokning
                </Button>

                {showAppForm && (
                  <div className="bg-white rounded-2xl p-6 border border-border/30 space-y-4">
                    <h3 className="font-semibold">Lägg till bokning</h3>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div>
                        <label className="text-sm text-muted-foreground">Typ *</label>
                        <Input
                          value={newApp.type}
                          onChange={(e) => setNewApp({ ...newApp, type: e.target.value })}
                          placeholder="T.ex. Läkarbesök, Tandläkare"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-muted-foreground">Läkare/Klinik</label>
                        <Input
                          value={newApp.doctor}
                          onChange={(e) => setNewApp({ ...newApp, doctor: e.target.value })}
                          placeholder="T.ex. Dr. Svensson"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-muted-foreground">Datum *</label>
                        <Input
                          type="date"
                          value={newApp.date}
                          onChange={(e) => setNewApp({ ...newApp, date: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="text-sm text-muted-foreground">Tid</label>
                        <Input
                          type="time"
                          value={newApp.time}
                          onChange={(e) => setNewApp({ ...newApp, time: e.target.value })}
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="text-sm text-muted-foreground">Plats</label>
                        <Input
                          value={newApp.location}
                          onChange={(e) => setNewApp({ ...newApp, location: e.target.value })}
                          placeholder="T.ex. Vårdcentralen Centrum"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="text-sm text-muted-foreground">Anteckning</label>
                        <Input
                          value={newApp.notes}
                          onChange={(e) => setNewApp({ ...newApp, notes: e.target.value })}
                          placeholder="T.ex. Ta med remiss, fasta..."
                        />
                      </div>
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button variant="outline" onClick={() => setShowAppForm(false)} className="rounded-full">
                        Avbryt
                      </Button>
                      <Button onClick={addAppointment} className="rounded-full">
                        Lägg till
                      </Button>
                    </div>
                  </div>
                )}

                {appointments.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">Inga bokningar registrerade.</p>
                ) : (
                  <div className="space-y-2">
                    {appointments.map(app => {
                      const isPast = new Date(app.date) < new Date();
                      return (
                        <div key={app.id} className={`bg-white p-4 rounded-xl border flex items-center gap-4 ${isPast ? 'border-gray-200 opacity-60' : 'border-border/30'}`}>
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isPast ? 'bg-gray-100 text-gray-500' : 'bg-emerald-100 text-emerald-600'}`}>
                            <Stethoscope className="w-5 h-5" />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-medium">{app.type}</h4>
                            <div className="flex flex-wrap gap-2 text-sm text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {new Date(app.date).toLocaleDateString('sv-SE', { weekday: 'short', day: 'numeric', month: 'short' })}
                              </span>
                              {app.time && (
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {app.time}
                                </span>
                              )}
                              {app.doctor && <span>• {app.doctor}</span>}
                            </div>
                            {app.notes && (
                              <p className="text-xs text-amber-600 flex items-center gap-1 mt-1">
                                <AlertCircle className="w-3 h-3" />
                                {app.notes}
                              </p>
                            )}
                          </div>
                          <button onClick={() => deleteAppointment(app.id)} className="p-2 text-gray-400 hover:text-red-500">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Disclaimer */}
        <p className="text-xs text-muted-foreground text-center px-4">
          ⚕️ Hälsologgen ersätter inte professionell medicinsk rådgivning. Kontakta alltid sjukvården vid hälsoproblem.
        </p>
      </main>
    </div>
  );
}
